/**
 * A member's access as `GET study/:id/people` sends it (users decisions 21, 32, 42 and 110). The
 * server works it out, groups replacing the own grant and then Manage study holding the other four
 * manage permissions, and this module is the only place the edc reads it. It holds no precedence
 * rule of its own: a client copy of that rule is what made the Team row and its expanded panel
 * disagree (client defect d1).
 *
 * The types match spdata-api's `src/users/effective.access.ts`; `Grant` is `./grant`'s.
 */

import type { Grant } from './grant';

/**
 * Where one field of a member's effective access comes from: their own grant, Manage study, or the
 * groups that grant it, by the same hex id as the member's `groups[].identifier`. A field the groups
 * decide and none grants has `{ groups: [] }`: the groups override the own grant there.
 */
export type Source = 'own' | 'manageStudy' | { groups: string[] };

/** One `Source` per field of the grant. */
export type GrantSource = Record<keyof Grant, Source>;

/** One member's access in the study. */
export interface MemberAccess {
  /** What their own rows grant. */
  own: Grant;
  /** What they can do. */
  effective: Grant;
  source: GrantSource;
  /** Whether a group decides every field a group can state, so the own grant is not in effect there. */
  inGroup: boolean;
}

/** Whether a field of a grant gives anything. `viewEntries: 'own'` is the floor every member has. */
export const grants = (value: Grant[keyof Grant]): boolean =>
  value === true || value === 'all' || (Array.isArray(value) && value.length > 0);

/** The fields paged-users adds to each member. Pending users carry none of them. */
export type NetworkMemberAccess = Partial<MemberAccess>;

/** A member's access off the wire, or nothing for a person the read sent none for, such as an invite. */
export const memberAccessOf = ({ own, effective, source, inGroup }: NetworkMemberAccess): MemberAccess | undefined =>
  own && effective && source && inGroup !== undefined ? { own, effective, source, inGroup } : undefined;
